import React from 'react';
import { Users, Award, Target, Shield } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Strokovnost",
      description: "Več kot 10 let izkušenj na področju nepremičnin in gradbeništva."
    },
    {
      icon: Award,
      title: "Kakovost",
      description: "Vsak projekt izvedemo z največjo skrbnostjo in pozornostjo do detajlov."
    },
    {
      icon: Shield,
      title: "Zanesljivost",
      description: "Naše stranke se lahko zanesejo na dogovorjene roke in kakovost izvedbe."
    },
    {
      icon: Users,
      title: "Partnerstvo",
      description: "Dolgoročni odnosi s strankami in poslovnimi partnerji so naša prednost."
    }
  ];

  const stats = [
    { number: "50+", label: "Uspešnih projektov" },
    { number: "€10M+", label: "Vrednost projektov" },
    { number: "10+", label: "Let izkušenj" },
    { number: "100%", label: "Zadovoljne stranke" }
  ];

  return (
    <section id="o-nas" className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            O podjetju <span className="text-atren-orange">ATREN INVEST</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Smo specializirano podjetje za razvoj nepremičnin, investicijsko vodenje projektov 
            in izvajanje premium gradbenih del. Naša strast so izzivni projekti in zadovoljne stranke.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Text Content */}
          <div className="animate-fade-in-up">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              Več kot desetletje izkušenj v nepremičninah
            </h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                ATREN INVEST je nastalo iz želje po ustvarjanju kakovostnih nepremičninskih rešitev. 
                Naša pot se je začela z manjšimi prenovi, danes pa uspešno vodimo kompleksne 
                investicijske projekte.
              </p>
              <p>
                Naš pristop temelji na strokovnosti, inovativnosti in osebnem pristopu do vsakega 
                projekta. Verjamemo, da so najbolje izvedeni projekti tisti, kjer se združijo 
                strokovno znanje, kakovostni materiali in pozornost do detajlov.
              </p>
              <p>
                Ponosni smo na naše reference in dolgoročne odnose s strankami, ki se k nam 
                vračajo za nove projekte in nas priporočajo svojim znancam.
              </p>
            </div>
          </div>

          {/* Statistics */}
          <div className="animate-scale-in">
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <Card key={index} className="text-center p-6 border-0 bg-gradient-subtle hover:shadow-primary transition-all duration-300">
                  <CardContent className="p-0">
                    <div className="text-3xl md:text-4xl font-bold text-atren-orange mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat.label}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="animate-fade-in-up">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-12">
            Naše <span className="text-atren-orange">vrednote</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card 
                key={index} 
                className="text-center p-6 border-0 bg-white hover:shadow-elegant transition-all duration-300 group animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-0">
                  <div className="w-16 h-16 bg-atren-orange/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-atren-orange/20 transition-colors duration-300">
                    <value.icon className="w-8 h-8 text-atren-orange" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-3 group-hover:text-atren-orange transition-colors duration-300">
                    {value.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mt-20 text-center max-w-4xl mx-auto animate-fade-in-up">
          <div className="bg-gradient-primary rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-6">
              Naša vizija
            </h3>
            <p className="text-lg leading-relaxed opacity-95">
              "Biti prepoznani kot vodilno podjetje za kakovostne nepremičninske rešitve v Sloveniji. 
              Naš cilj je ustvarjanje vrhunskih projektov, ki presegajo pričakovanja strank in 
              dvigujejo standarde v panogi."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;